import dayjs from 'dayjs'
import React, { useState } from 'react'

import { Button, Calendar, Select, useMediaQueryContext } from '@fourthwall/components'

import { getRangeLabel } from '../../../../../utils/getRangeLabel'
import type { DateRangeOption, DateRangeValue } from '../../AnalyticsDateRangeSelect.types'
import * as S from './DateRangeOptions.styled'

type DateRangeOptionsProps = {
  value: DateRangeValue
  options: DateRangeOption[]
  onSelect: (value: DateRangeValue) => void
  onClose?: () => void
}

export const DateRangeOptions = ({ value, options, onSelect, onClose }: DateRangeOptionsProps) => {
  const media = useMediaQueryContext()
  const [selectedValue, setSelectedValue] = useState<DateRangeValue>(value)
  const [calendarDates, setCalendarDates] = useState<Date[]>(value.dates)

  const handleApply = () => {
    onSelect(selectedValue)
    onClose?.()
  }

  const handleDatesChange = (dates: Date[]) => {
    // Update calendar dates for display
    setCalendarDates(dates)

    // Check if selected dates match any predefined option
    const matchingOption = options.find((option) => {
      if (!option.dates) return false

      const optionStartDate = dayjs(option.dates[0])
      const optionEndDate = dayjs(option.dates[1])
      const selectedStartDate = dayjs(dates[0])
      const selectedEndDate = dayjs(dates[1])

      return (
        optionStartDate.isSame(selectedStartDate, 'day') &&
        optionEndDate.isSame(selectedEndDate, 'day')
      )
    })

    if (matchingOption) {
      setSelectedValue(matchingOption)
      setCalendarDates(matchingOption.dates)
    } else {
      // Set first date to start of day (00:00:00) and second date to end of day (23:59:59)
      const normalizedDates: [Date, Date] = [
        dayjs(dates[0]).startOf('day').toDate(),
        dayjs(dates[1]).endOf('day').toDate(),
      ]
      setSelectedValue({ dates: normalizedDates })
    }
  }

  const handleSelectValueChange = (value: string) => {
    const selectedOption = options.find((option) => option.value === value)

    if (selectedOption) {
      setSelectedValue(selectedOption)
      setCalendarDates(selectedOption.dates)
    }
  }

  const renderOptions = () => {
    if (!media.tablet) {
      return (
        <S.OptionsSelect>
          <Select
            value={selectedValue.value}
            label="Select range"
            size="small"
            options={options.map(({ value, label }) => ({
              value,
              label,
            }))}
            onValueChange={handleSelectValueChange}
          />
        </S.OptionsSelect>
      )
    }

    return (
      <S.OptionsList maxHeight={354} maskPercentage={8} hideScrollbar>
        {options.map((option) => (
          <S.OptionsListItem
            key={option.value}
            $selected={selectedValue.value === option.value}
            onClick={() => {
              setSelectedValue(option)
              setCalendarDates(option.dates)
            }}
          >
            {option.label}
          </S.OptionsListItem>
        ))}
      </S.OptionsList>
    )
  }

  return (
    <S.Container>
      <S.Main>
        {renderOptions()}
        <S.CalendarWrapper>
          <Calendar
            key={calendarDates[0]?.toString()}
            type="range"
            bare
            selectedDates={calendarDates}
            maxDate={new Date()}
            showTimeInputs={false}
            onDatesChange={handleDatesChange}
          />
        </S.CalendarWrapper>
      </S.Main>

      <S.Footer>
        <S.DateRangeLabel>{getRangeLabel(selectedValue.dates)}</S.DateRangeLabel>
        <S.Actions>
          <Button
            label="Cancel"
            size={media.tablet ? 'small' : 'medium'}
            appearance="secondary"
            fullWidth={!media.tablet}
            onClick={onClose}
          />
          <Button
            label="Apply"
            size={media.tablet ? 'small' : 'medium'}
            appearance="primary"
            fullWidth={!media.tablet}
            onClick={handleApply}
          />
        </S.Actions>
      </S.Footer>
    </S.Container>
  )
}
